// DeviceProvider.tsx
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";

type MsgCallback = (msg: string) => void;

export interface DeviceContextValue {
  connected: boolean;
  log: string[];
  connect: (url: string) => void;
  disconnect: () => void;
  send: (message: string) => void;
  onReceive: (id: string, callback: MsgCallback | null) => void;
}

const DeviceContext = createContext<DeviceContextValue | undefined>(undefined);

const STORAGE_KEY = "Device:lastUrl";
const RECONNECT_MAX_DELAY_MS = 30_000; // 30s

export const DeviceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [connected, setConnected] = useState(false);
  const [log, setLog] = useState<string[]>([]);

  // refs that persist between renders
  const socketRef = useRef<WebSocket | null>(null);
  const receiveCallbacksRef = useRef<Map<string, MsgCallback>>(new Map());
  const manualDisconnectRef = useRef(false);
  const reconnectTimerRef = useRef<number | null>(null);
  const attemptCountRef = useRef(0);
  const urlRef = useRef<string | null>(null);

  const logMessage = useCallback((msg: string) => {
    const ts = new Date().toLocaleTimeString();
    setLog((prev) => [`[${ts}] ${msg}`, ...prev]);
  }, []);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    clearReconnectTimer();
    const attempts = attemptCountRef.current;
    // exponential backoff: 1s, 2s, 4s, 8s... capped
    const delay = Math.min(RECONNECT_MAX_DELAY_MS, 1000 * Math.pow(2, Math.max(0, attempts - 1)));
    logMessage(`Scheduling reconnect attempt #${attempts + 1} in ${Math.round(delay)}ms`);
    reconnectTimerRef.current = window.setTimeout(() => {
      reconnectTimerRef.current = null;
      attemptCountRef.current += 1;
      const url = urlRef.current;
      if (url && !manualDisconnectRef.current) {
        connect(url);
      }
    }, delay);
  }, [clearReconnectTimer, logMessage]);

  // connect implementation
  const connect = useCallback(
    (url: string) => {
      // record that this connection attempt is intentional
      manualDisconnectRef.current = false;

      // if a socket exists, close it gracefully first
      if (socketRef.current) {
        try {
          socketRef.current.close();
        } catch {}
        socketRef.current = null;
      }

      urlRef.current = url;
      // persist url so we can reconnect after page refresh
      try {
        localStorage.setItem(STORAGE_KEY, url);
      } catch {}

      logMessage(`Connecting to ${url}...`);

      let ws: WebSocket;
      try {
        ws = new WebSocket(url);
      } catch (err) {
        logMessage(`WebSocket constructor error: ${(err as Error).message}`);
        // schedule reconnect if appropriate
        attemptCountRef.current += 1;
        scheduleReconnect();
        return;
      }

      socketRef.current = ws;

      ws.onopen = () => {
        attemptCountRef.current = 0; // reset attempts on successful connect
        clearReconnectTimer();
        setConnected(true);
        logMessage(`Connected to ${url}`);
      };

      ws.onclose = (ev) => {
        socketRef.current = null;
        setConnected(false);
        logMessage(`Disconnected from ${url} (code=${ev.code})`);
        // Only auto-reconnect if this wasn't a manual disconnect
        if (!manualDisconnectRef.current && urlRef.current) {
          attemptCountRef.current = Math.max(0, attemptCountRef.current); // ensure non-negative
          attemptCountRef.current += 1;
          scheduleReconnect();
        }
      };

      ws.onerror = (err) => {
        logMessage(`WebSocket error: ${JSON.stringify(err)}`);
      };

      ws.onmessage = (event) => {
        const data = typeof event.data === "string" ? event.data : JSON.stringify(event.data);
        logMessage(`Received: ${data}`);
        // dispatch to registered callbacks
        receiveCallbacksRef.current.forEach((cb) => {
          try {
            cb(data);
          } catch (e) {
            console.error("onReceive handler error", e);
          }
        });
      };
    },
    [clearReconnectTimer, logMessage, scheduleReconnect]
  );

  const disconnect = useCallback(() => {
    manualDisconnectRef.current = true;
    clearReconnectTimer();
    urlRef.current = null;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
    if (socketRef.current) {
      try {
        socketRef.current.close();
      } catch {}
      socketRef.current = null;
    }
    setConnected(false);
    logMessage("Disconnected (manual)");
  }, [clearReconnectTimer, logMessage]);

  const send = useCallback(
    (message: string) => {
      console.log(`[DeviceProvider] Sending: ${message}`);
      const ws = socketRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(message);
        logMessage(`Sent: ${message}`);
      } else {
        console.warn(`[DeviceProvider] WebSocket not connected — message not sent: ${message}`);
        logMessage("WebSocket not connected — buffering not implemented");
      }
    },
    [logMessage]
  );

  // register/update/remove callback by id
  const onReceive = useCallback((id: string, callback: MsgCallback | null) => {
    if (callback) {
      receiveCallbacksRef.current.set(id, callback);
    } else {
      receiveCallbacksRef.current.delete(id);
    }
  }, []);

  // Auto-reconnect after page refresh: try last url from localStorage
  useEffect(() => {
    try {
      const last = localStorage.getItem(STORAGE_KEY);
      if (last) {
        // small timeout to ensure consumers mounted can attach callbacks if needed
        // 200ms is arbitrary and conservative
        const t = window.setTimeout(() => {
          if (!manualDisconnectRef.current) connect(last);
        }, 200);
        return () => clearTimeout(t);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    } catch (e) {
      // ignore storage errors
    }
    // no cleanup
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  // cleanup on unmount
  useEffect(() => {
    return () => {
      clearReconnectTimer();
      if (socketRef.current) {
        try {
          socketRef.current.close();
        } catch {}
      }
    };
  }, [clearReconnectTimer]);

  const value = useMemo<DeviceContextValue>(
    () => ({
      connected,
      log,
      connect,
      disconnect,
      send,
      onReceive,
    }),
    [connected, log, connect, disconnect, send, onReceive]
  );

  return <DeviceContext.Provider value={value}>{children}</DeviceContext.Provider>;
};

export const useDevice = (): DeviceContextValue => {
  const ctx = useContext(DeviceContext);
  if (!ctx) throw new Error("useDevice must be used within a DeviceProvider");
  return ctx;
};
